
import { Vue, Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { Route, NavigationGuardNext } from "vue-router";
import { attachCcxFrameHost } from "@/utils/ccx-frame-host";

// 让 Vue 类组件识别离开路由前的生命周期；若项目已统一注册，可合并到现有注册处。
Component.registerHooks(["beforeRouteLeave"]);
// 测试包固定已部署的正常聊天入口，不沿用旧诊断环境变量；无需 IT 配置。
const CHAT_URL = process.env.VUE_APP_AI_URL;
import { pxToVw } from '@/utils/pxToVw'
import { setAllowsBackGesture } from "@/utils/h5ToApp";

@Component({
  components: {},
})
export default class Ai extends Vue {
  @Getter aiSourceUrl!: string;
  @Getter token!: string;
  @Getter userId!: string;
  @Getter companyId!: string;
  @Getter multiStoreList!: string[];
  @Getter isMultiStore!: boolean;
  @Getter topSafetyHeight!: number

  // 通信只调整助手可见区及本页返回键的位置，保持 iframe 原始尺寸。
  backTop = 0;
  viewportHost: ReturnType<typeof attachCcxFrameHost> | null = null;
  iframeUrl = "";
  timer: ReturnType<typeof setTimeout> | null = null;
  controller: AbortController | null = null;

  get topSafetyHeightCss(): string {
    return Number.isFinite(this.topSafetyHeight) && this.topSafetyHeight >= 0
      ? pxToVw(this.topSafetyHeight) : "env(safe-area-inset-top, 0px)"
  }

  goBack() {
    this.$router.back();
  }

  created() {
    const storeIds = this.isMultiStore
      ? this.multiStoreList.join(",")
      : this.companyId;
    const from = String((this.$route.query && this.$route.query.from) || '')
    const val = encodeURIComponent(
      `Opuser=${this.userId}&storeIds=${storeIds}&Authorization=bearer ${this.token}&from=${from}`,
    );
    this.iframeUrl = CHAT_URL + `?params=${val}`;
    this.checkServer();
  }

  async checkServer() {
    this.controller = new AbortController();
    this.timer = setTimeout(() => {
      // 10s保底
      this.controller?.abort();
      this.showDialog();
    }, 10000);
    try {
      await fetch(CHAT_URL, {
        method: "HEAD",
        mode: "no-cors",
        signal: this.controller.signal,
      });
    } catch (error) {
      this.showDialog();
    } finally {
      if (this.timer !== null) clearTimeout(this.timer);
    }
  }

  showDialog() {
    this.$dialog.alert({
      message: "资源加载超时，请重试",
    });
  }

  startViewportHost() {
    if (this.viewportHost) return;
    const iframe = this.$refs.aiFrame as HTMLIFrameElement;
    if (iframe) {
      // 只提取来源，不记录或发送带登录参数的完整地址。
      this.viewportHost = attachCcxFrameHost(iframe, new URL(this.iframeUrl, window.location.href).origin, {
        // 读取现有安全区换算后的 CSS 像素，不能直接把 pxToVw 返回的 vw 当作 px。
        topInset: () => {
          const container = this.$refs.aiContainer as HTMLElement;
          return container ? parseFloat(window.getComputedStyle(container).marginTop) || 0 : 0;
        },
        // 仅检测开启时读取返回键实际绘制位置，不上传文本或链接。
        readBackTop: () => (this.$refs.aiBack as HTMLElement | undefined)?.getBoundingClientRect().top,
        onGeometry: geometry => {
          if (!geometry) { this.backTop = 0; return; }
          const container = this.$refs.aiContainer as HTMLElement;
          if (!container) return;
          const frameRect = iframe.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const frameScale = iframe.offsetHeight > 0 ? frameRect.height / iframe.offsetHeight : 1;
          const containerScale = container.offsetHeight > 0 ? containerRect.height / container.offsetHeight : 1;
          if (!(containerScale > 0)) return;
          // 将助手标题的顶部换算到返回键所在容器；外层滚动时两者保持对齐。
          const titleTop = frameRect.top + (iframe.clientTop + geometry.offsetTop) * frameScale;
          this.backTop = Math.max(0, (titleTop - containerRect.top) / containerScale - container.clientTop);
        },
      });
    }
  }

  stopViewportHost() {
    this.viewportHost?.dispose();
    this.viewportHost = null;
    this.backTop = 0;
  }

  beforeRouteLeave(_to: Route, _from: Route, next: NavigationGuardNext) {
    // 覆盖页面返回按钮、路由返回等入口，先等待助手释放焦点；最长等待 250 毫秒。
    if (this.viewportHost) this.viewportHost.close().then(() => next(), () => next());
    else next();
  }

  activated() {
    // 兼容 keep-alive 页面重新进入。
    this.$nextTick(() => this.startViewportHost());
  }

  deactivated() {
    this.stopViewportHost();
  }

  beforeDestroy() {
    this.stopViewportHost();
  }

  destroyed() {
    this.timer && clearTimeout(this.timer);
    this.controller?.abort();
  }

  mounted() {
    setAllowsBackGesture('1');
    this.startViewportHost();
  }
}
